import { Component, ElementRef, Input, OnDestroy, Optional, Self, OnInit, Output, EventEmitter, ViewChild, HostListener, DoCheck } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, ControlValueAccessor, NgControl} from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import dayjs from 'dayjs';
import { reverseDate } from './reverse-date';

export interface IconAction {
	handler?: Function;
	onHover?: Function;
	icon: Function;
	title: Function;
}

@Component({
// tslint:disable-next-line: component-selector
	selector: 'tr2-fecha',
	templateUrl: 'fecha.component.html',
	styleUrls: ['fecha.component.css'],
	providers: [
		{ provide: MatFormFieldControl, useExisting: Fecha }
	],
	/** Definimos atributos para el html del componente */
	host: {
		'[class.fecha-floating]': 'shouldLabelFloat',
		'[id]': 'id',
		'[attr.aria-describedby]': 'describedBy'
	}
})
export class Fecha implements MatFormFieldControl<dayjs.Dayjs>, OnDestroy, ControlValueAccessor, OnInit, DoCheck {

	/** Variable que setea el placeholder de la entrada */
	@Input()
	get placeholder(): string {
		return this._placeholder;
	}
	set placeholder(value: string) {
		this._placeholder = value;
		this.stateChanges.next();
	}

	/** Variable que asigna el atributo name al input */
	@Input()
	get name(): any[] {
		return this._name;
	}
	set name(value: any[]) {
		this._name = value;
		this.stateChanges.next();
	}

	/** Variable que indica si el campo es requerido para el formulario */
	@Input()
	get required(): boolean {
		if (this._required) {
			return this._required;
		}

		// cuando el control retorna un error desde la validación con un valor vacío
		if (this.ngControl && this.ngControl.control && this.ngControl.control.validator) {
			const emptyValueControl = Object.assign({}, this.ngControl.control);
			(emptyValueControl as any).value = null;
			return 'required' in (this.ngControl.control.validator(emptyValueControl) || {});
		}
		return false;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
		this.stateChanges.next();
	}

	/** Variable para setear si el campo de entrada está deshabilitado o no */
	@Input()
	get disabled(): boolean {
		if (this.ngControl && this.ngControl.control && this.ngControl.control.disabled) {
			this._disabled = this.ngControl.control.disabled;
		}
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this._disabled ? this.form.disable() : this.form.enable();
		this.stateChanges.next();
	}

	/** Variable en donde se captura el valor de la entrada */
	@Input()
	get value(): any {
		return this.control.value;
	}
	set value(val: any) {
		this.control.setValue(val);
		this.stateChanges.next();
	}

	/** Función que indica si la entrada del formulario esta vacía */
	get empty() {
		return !this.control || this.control.value === null || this.control.value === '';
	}

	/** Función que indica si la etiqueta de la entrada pasa a flotante o no */
	get shouldLabelFloat() {
		return this.focused || !this.empty;
	}


	/** Constructor de la clase */
	constructor(
		fb: FormBuilder,
		private fm: FocusMonitor,
		private elRef: ElementRef<HTMLElement>,
		@Optional() @Self() public ngControl: NgControl,
	) {
		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}

		fm.monitor(elRef, true).subscribe(origin => {
			this.focused = !!origin;
			this.stateChanges.next();
		});
	}

	/****** Definimos variables de clase ******/
	static nextId = 0;
	form: FormGroup;
	stateChanges = new Subject<void>();
	focused = false;
	errorState = false;
	controlType = 'tr2-fecha';
	id = `tr2-fecha-${Fecha.nextId++}`;
	describedBy = '';
	control = new FormControl(null);
	private _placeholder: string;
	private _name: any[];
	private _required = false;
	private _disabled = false;


	@Input() readonly = false;

	/** Variable que define qué se muestra en el calendario cuando se abre por primera vez */
	@Input() startView = 'month';

	/** Variable que define en qué fecha se muestra en el calendario cuando se abre por primera vez */
	@Input() startAt = null; // dayjs(new Date(1990, 0, 1));

	/** Indica la fecha mínima a seleccionar */
	@Input() min = null; // dayjs(new Date(2019, 4, 20));

	/** Indica la fecha máxima a seleccionar */
	@Input() max = null; // dayjs(new Date(2019, 4, 28));

	/** Permite deshabilitar el pop up, pero no el input */
	@Input() disabledPopup = false;

	/** Permite deshabilitar el input, pero no el popup */
	@Input() disabledInput = false;

	/** Permite habilitar el modo touch */
	@Input() touchUi = false;

	/** Permite setear el atributo "title" del icono del calendario */
	@Input() datepickertoggletitle = 'Show Calendar';

	/** Permite cambiar el icono del calendario */
	@Input() openIcon = 'event';

	/** tabindex, no utiliza directiva FocusNext */
	@Input() tab = -1;

	/** Indica el formato de la fecha */
	// @Input() formato = 'DD/MM/YYYY';

	/** Permite la apertura del popup al hacer foco sobre el input */
	@Input() onInputPopup: false;

	/** Método que emite evento al cambiar la fecha seleccionada */
	@Output() cambiaFecha = new EventEmitter();

	@ViewChild('input', { static: true }) inputElement: ElementRef;

	// @ViewChild('limpiarBtn') limpiarBtn: MatButton;

	@ViewChild(MatDatepicker, { static: true }) datepicker: MatDatepicker<dayjs.Dayjs>;

	/** Permite definir un filtro para validar las fechas a seleccionar
	 *
	 * (d: dayjs.Dayjs): boolean => {
		const day = d.day();
		// Prevent Saturday and Sunday from being selected.
		return day !== 0 && day !== 6;
	  }
	 *
	 */
	@Input() Filter = () => true;

	/** Permite resaltar fechas en el calendario */
	@Input() claseResaltaFecha = () => {
		return undefined;
	}

	/****** Métodos de clase ******/
	ngOnInit() {
		// setemos y enlazamos el formControl al del input del autocomplete
		this.control = this.ngControl.control as FormControl;

		// para acomodar el icono, ya que se trata aparentemente de un subcompoponente y no podemos definirle clases de estilo
		this.elRef.nativeElement.querySelector('.fecha-btn .mat-icon-button').setAttribute('style', 'font-size: inherit; width: 1em; height: 1.125em; line-height: 1.125;');
	}

	ngOnDestroy() {
		this.stateChanges.complete();
		this.fm.stopMonitoring(this.elRef);
	}

	ngDoCheck() {
		this.errorState = this.ngControl.invalid && (this.ngControl.touched || this.ngControl.dirty);
	}

	setDescribedByIds(ids: string[]) {
		this.describedBy = ids.join(' ');
	}

	/// tal vez deberia ser mas especifico que solo input?
	onContainerClick(event: MouseEvent) {
		if ((event.target as Element).tagName.toLowerCase() != 'input') {
			this.elRef.nativeElement.querySelector('input').focus();
		}
	}

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	writeValue() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnChange() { }

	/** Funcion implemetnada para la interfaz ControlValueAccesor*/
	registerOnTouched() { }

	onCambiaFecha(event) {
		const inputValue: string = reverseDate(event.target._elementRef.nativeElement.value);
		if (!inputValue.includes('Invalid date') || !inputValue.includes('Fecha inválida')) {
			if (dayjs(this.control.value).isValid()) {
				this.cambiaFecha.emit(this.control.value); // Emito el dayjs
			} else {
				this.limpiar();
			}
		} else {
			this.limpiar();
		}
	}

	limpiar() {
		this.control.reset();
		this.cambiaFecha.emit(this.control.value);
	}

	check() {
		if (this.control && (this.control.invalid || !this.control.value)) {
			this.inputElement.nativeElement.value = '';
		}
	}

	setHoy() {
		this.cambiaFecha.emit(dayjs()); // Emito el dayjs
	}

	esHoy() {
		if (this.control && !this.control.invalid && this.control.value && (this.control.value as dayjs.Dayjs).isSame(dayjs(), 'day')) {
			return true;
		} else {
			return false;
		}
	}

	getDayjs(): dayjs.Dayjs {
		return this.datepicker._selected;
	}

	setDayjs(date: dayjs.Dayjs) {
		if (dayjs(date).isValid()) {
			this.cambiaFecha.emit(date); // Emito el dayjs
		} else {
			this.limpiar();
		}
	}

	focus() {
		this.inputElement.nativeElement.focus();
	}

	onFocus(event) {
		if (event.target.select) {
			event.target.select();
		}
		if (this.onInputPopup) {
			this.datepicker.open();
		}
	}

	onBlur(event) {
		this.check();
		this.setTouched();
	}

	/** Método que hace que aparezca el mat-error correctamente */
	setTouched(): void {
		if (this.control && !this.control.touched) {
			this.control.markAsTouched();
			this.errorState = this.control.invalid;
		}
		this.stateChanges.next();
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent) {
		if (event.keyCode == 27) {
			event.preventDefault();
			event.stopPropagation();
			this.limpiar();
		}
	}
}
